import { ReactNode } from 'react';
import { CoreBox, CoreTypography } from '@youscience/core';
import {
  CounselorFormStatus,
  FormActionsStatus,
  Members,
  Roles,
  TeacherRecommendationStatus,
  DeadlineTypeMap,
} from '@youscience/commonapp-service-common';

import ProgressCircle from '@components/ProgressCircle';
import { Chip } from '@components/Chip';

import useCommonAppAdminStore from '@store/CommonAppAdminStore';

import { formatDateWithFallback } from '@utils/formatter';

import { TEACHER_EVALUATION_STATUS_DATA } from '@constants/recommenderWorkspace';

import { Counselor } from '@interfaces/recommenderWorkspace';

import FlagIcon from '@images/sports-score.svg?react';
import ChevronBottomIcon from '@images/chevron-bottom.svg?react';

import {
  sxStyles,
  StyledHeaderWrap,
  StyledProgressWrap,
  StyledInfoSection,
  StyledInfoItemsWrap,
  StyledInfoItemWrap,
  StyledToggle,
} from './SchoolCardHeader.styles';

export const renderInfoItem = (title: string, value: string, icon?: ReactNode) => {
  return (
    <StyledInfoItemWrap data-testid={`info-item-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      {icon ? <CoreBox>{icon}</CoreBox> : null}

      <CoreTypography
        sx={sxStyles.infoItemTitle}
        variant="subtitle2"
        component="h4"
        data-testid={`info-item-title-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {title}
      </CoreTypography>

      <CoreTypography
        sx={sxStyles.infoItemValue}
        variant="body2"
        data-testid={`info-item-value-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {value}
      </CoreTypography>
    </StyledInfoItemWrap>
  );
};

interface SchoolCardHeaderProps {
  isFullCard: boolean;
  setIsFullCard: (status: boolean) => void;
  card: Members;
  cardHeaderStatus: TeacherRecommendationStatus | null;
  openedSchoolCardId: number | null;
  setOpenedSchoolCardId: (id: number | null) => void;
}

export const SchoolCardHeader = (props: SchoolCardHeaderProps) => {
  const {
    isFullCard,
    setIsFullCard,
    card: { memberId, collegeName, deadline, counselor, selectedRound },
    cardHeaderStatus,
    openedSchoolCardId,
    setOpenedSchoolCardId,
  } = props;

  const { userRoles } = useCommonAppAdminStore((store) => store);

  const isCounselorRole = userRoles?.includes(Roles.COUNSELOR);

  const isSchoolCardOpened = openedSchoolCardId === memberId;

  const toggleFullCardView = () => {
    if (isSchoolCardOpened) {
      setIsFullCard(false);
      setOpenedSchoolCardId(null);
    } else {
      setIsFullCard(true);
      setOpenedSchoolCardId(memberId);
    }
  };

  const selectedRoundDescription = selectedRound ? (DeadlineTypeMap.get(selectedRound) ?? '') : '';

  return (
    <StyledHeaderWrap>
      <StyledInfoSection>
        {isCounselorRole ? progressIndicator(counselor) : null}

        <CoreBox sx={sxStyles.schoolInfo}>
          <CoreTypography variant="h5">{collegeName}</CoreTypography>

          <StyledInfoItemsWrap>
            {selectedRoundDescription ? (
              <>
                <Chip title={selectedRoundDescription} color="disabled" />
                <CoreBox sx={sxStyles.infoItemDivider} />
              </>
            ) : null}

            {deadline ? (
              <>
                {renderInfoItem('Deadline', formatDateWithFallback(deadline), <FlagIcon />)}
                {cardHeaderStatus ? <CoreBox sx={sxStyles.infoItemDivider} /> : null}
              </>
            ) : null}
            {cardHeaderStatus
              ? renderInfoItem(
                  'Teacher evaluation',
                  TEACHER_EVALUATION_STATUS_DATA[cardHeaderStatus]?.title,
                  TEACHER_EVALUATION_STATUS_DATA[cardHeaderStatus]?.icon,
                )
              : null}
          </StyledInfoItemsWrap>
        </CoreBox>
      </StyledInfoSection>

      {userRoles?.includes(Roles.COUNSELOR) ? (
        <StyledToggle isOpen={isFullCard && isSchoolCardOpened} onClick={toggleFullCardView}>
          <ChevronBottomIcon data-testid={isFullCard ? 'chevronTop' : 'chevronRight'} />
        </StyledToggle>
      ) : null}
    </StyledHeaderWrap>
  );
};

const progressIndicator = (counselor: Counselor) => {
  const applicableRequirementsCount = counselor.forms?.filter(
    (item) => item.status !== CounselorFormStatus.NOT_APPLICABLE,
  ).length;

  if (!applicableRequirementsCount) {
    return null;
  }

  const submittedRequirementsCount = counselor.forms?.filter(
    (item) => item.formActionStatus === FormActionsStatus.SUBMITTED,
  ).length;

  const fillingProgressCircle = (submittedRequirementsCount * applicableRequirementsCount) / 100;

  return (
    <StyledProgressWrap>
      <ProgressCircle
        filling={fillingProgressCircle}
        size={56}
        label={`${submittedRequirementsCount}/${applicableRequirementsCount}`}
      />

      <CoreTypography data-testid="progressCircleTitle" variant="caption">
        Tasks completed
      </CoreTypography>
    </StyledProgressWrap>
  );
};
